import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/authStore';
import { queryClient } from './queryClient';

const API_BASE = '/api/v1';

// One axios instance for every API call. `withCredentials: true` is required
// for the httpOnly refresh-token cookie to ride along on /auth/refresh.
export const apiClient = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach the in-memory access token as `Authorization: Bearer …` on every
// request. The token comes from Zustand (not localStorage — spec §1.7).
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// ─── Auto-refresh on 401 ────────────────────────────────────────────────────
// When any non-auth request returns 401:
//   1. Pause all subsequent failed requests in a queue.
//   2. Fire POST /auth/refresh once.
//   3. On success: update the auth store with the new access token and replay
//      every queued request with the new Authorization header.
//   4. On failure: clear the auth store; reject all queued requests so their
//      callers can redirect to /login.
// We intentionally bypass this flow for /auth/login, /auth/refresh, /auth/logout
// — a 401 from those is the real answer, not a refresh trigger.

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (err: unknown) => void;
  config: InternalAxiosRequestConfig;
}

let refreshing: Promise<string | null> | null = null;
const queue: PendingRequest[] = [];

async function callRefresh(): Promise<string | null> {
  try {
    const res = await axios.post<{ accessToken: string }>(
      `${API_BASE}/auth/refresh`,
      {},
      { withCredentials: true },
    );
    return res.data.accessToken;
  } catch (err) {
    // Every catch logs (per project rules) — even on the client.
    // We log to console.warn at the boundary; production code may wire a remote
    // logger later. The originating 401 propagates back to the caller.
    console.warn('[apiClient] refresh request failed', err);
    return null;
  }
}

function flushQueue(newToken: string | null, errorIfNoToken: unknown): void {
  while (queue.length > 0) {
    const pending = queue.shift();
    if (!pending) break;
    if (newToken) {
      pending.config.headers.set('Authorization', `Bearer ${newToken}`);
      apiClient.request(pending.config).then(pending.resolve).catch(pending.reject);
    } else {
      pending.reject(errorIfNoToken);
    }
  }
}

function isAuthRoute(url: string | undefined): boolean {
  if (!url) return false;
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/logout')
  );
}

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const cfg = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    if (
      error.response?.status === 401 &&
      cfg &&
      !cfg._retry &&
      !isAuthRoute(cfg.url)
    ) {
      cfg._retry = true;

      return new Promise((resolve, reject) => {
        queue.push({ resolve, reject, config: cfg });
        if (!refreshing) {
          refreshing = callRefresh().then((token) => {
            refreshing = null;
            if (token) {
              useAuthStore.getState().setAccessToken(token);
              // /auth/refresh returns only the new accessToken, not the
              // user payload — invalidate /me so AuthGuard's useMeQuery
              // re-fetches and `authStore.user` is repopulated from the
              // authoritative source. Without this, a silent refresh
              // can leave `user` stale or null even after the token is
              // restored.
              queryClient.invalidateQueries({ queryKey: ['me'] });
            } else {
              // Refresh failed — let the original request reject so
              // AuthGuard can transition out of its loading skeleton
              // and route to /login. Query cache cleanup happens on
              // the next successful login, not while the bootstrap
              // /me query is still resolving.
              useAuthStore.getState().logout();
            }
            flushQueue(token, error);
            return token;
          });
        }
      });
    }

    return Promise.reject(error);
  },
);
